import { getMatchedSelectionsFromLineItem, parseSearchPrefix, xactimateBadgeFromSearchData } from "@/components/LineItemSearchUtils";
import type { FindSelectOptions } from "@/components/input/FindSelect";
import type { LineItemSearchData } from "@/interfaces/line-item-elements-response";
import type { SearchMode } from "@rebuild/shared";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Builds a compound key that uniquely identifies a search result row. The
 * backend can return multiple rows for the same line item id — one per
 * xactimate mapping permutation (e.g. "Walls Paint 1 coat" vs "Walls Paint 2
 * coats"). React needs a stable unique key and we need a way to recover the
 * specific permutation that was selected from the dropdown.
 */
const buildOptionValue = (item: LineItemSearchData): string => `${item.id}|${item.acceptedLineString ?? ""}`;

export const MIN_SEARCH_LENGTH = 3;

export const useLineItemOptions = (onSearchLineItem: (search: string, searchMode?: SearchMode) => Promise<LineItemSearchData[]>) => {
  const [items, setItems] = useState<LineItemSearchData[]>([]);
  const [options, setOptions] = useState<FindSelectOptions[]>([]);
  const [itemSearchSelected, setItemSearchSelected] = useState<LineItemSearchData | undefined>();
  const [showCreateItem, setShowCreateItem] = useState(false);
  const [lineItemTextToCreate, setLineItemTextToCreate] = useState<string>("");
  const pendingSearchSelectionLabelRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    return () => {
      setOptions([]);
    };
  }, []);

  const onFindOptions = useCallback(
    (searchTerm: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      const { mode, query } = parseSearchPrefix(searchTerm);
      const trimmedQuery = query.trim();

      // Don't fire search for empty query after prefix
      if (!trimmedQuery) {
        setOptions([]);
        setItems([]);
        setLineItemTextToCreate("");
        return;
      }

      // In default text mode require at least 3 characters to avoid hammering
      // the backend on every keystroke. CAT/SEL code searches are short by
      // nature (e.g. "DRY") so they bypass this threshold.
      if (mode === "all" && trimmedQuery.length < MIN_SEARCH_LENGTH) {
        setOptions([]);
        setItems([]);
        setLineItemTextToCreate("");
        return;
      }

      debounceRef.current = setTimeout(async () => {
        const itemsSearched = await onSearchLineItem(trimmedQuery, mode);

        const newOptions: FindSelectOptions[] = itemsSearched.map((item) => {
          // When the row carries a specific xactimate-mapping permutation (i.e.
          // the backend returned this row because a mapping matched), we display
          // that exact permutation verbatim so "Walls Paint 1 coat" and "Walls
          // Paint 2 coats" appear as distinct entries rather than collapsing to
          // a single default label. Bare line item rows (no mapping hit) fall
          // back to the query-matched element selections.
          const hasAcceptedLineString = !!item.acceptedLineString?.trim();
          const label = hasAcceptedLineString
            ? item.acceptedLineString!.trim()
            : getMatchedSelectionsFromLineItem(item.elements, mode === "all" ? trimmedQuery : "").selections.join(" ");

          const badge = xactimateBadgeFromSearchData(item);
          // In text search mode pass the raw query so FindSelectOptionLabel can
          // bold every matching word independently. CAT/SEL searches don't need
          // in-label highlighting (the badge carries the match).
          const matchSegment = mode === "all" ? trimmedQuery : undefined;
          return {
            value: buildOptionValue(item),
            label,
            parent: item.category,
            badge,
            matchSegment,
          };
        });

        setOptions(newOptions);
        setItems(itemsSearched);
        setLineItemTextToCreate(trimmedQuery);
      }, 300);
    },
    [onSearchLineItem],
  );

  const onSelectItem = useCallback(
    (compoundValue: string, selectedLabel?: string) => {
      // `compoundValue` is `${id}|${acceptedLineString ?? ""}` — see
      // `buildOptionValue`. We split on the first `|` only so accepted line
      // strings that themselves contain `|` round-trip correctly.
      const sepIdx = compoundValue.indexOf("|");
      const id = sepIdx === -1 ? compoundValue : compoundValue.slice(0, sepIdx);
      const acceptedLineString = sepIdx === -1 ? "" : compoundValue.slice(sepIdx + 1);
      const currentItem = items.find((item) => item.id === id && (item.acceptedLineString ?? "") === acceptedLineString);
      // Prefer the exact accepted permutation (the canonical text the
      // xactimate mapping is keyed to) so downstream code can derive the
      // right per-element selections.
      pendingSearchSelectionLabelRef.current = currentItem?.acceptedLineString?.trim() || selectedLabel?.trim() || undefined;
      setItemSearchSelected(currentItem);
      setLineItemTextToCreate("");
    },
    [items],
  );

  const matchedPermutation = useCallback((): string | undefined => {
    const v = pendingSearchSelectionLabelRef.current;
    pendingSearchSelectionLabelRef.current = undefined;
    return v;
  }, []);

  const reset = useCallback(() => {
    pendingSearchSelectionLabelRef.current = undefined;
    setItems([]);
    setOptions([]);
    setItemSearchSelected(undefined);
    setLineItemTextToCreate("");
  }, []);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  return {
    options,
    onFindOptions,
    onSelectItem,
    matchedPermutation,
    itemSearchSelected,
    setItemSearchSelected,
    reset,
    showCreateItem,
    setShowCreateItem,
    lineItemTextToCreate,
    setLineItemTextToCreate,
  };
};
